import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
  type ReactNode,
} from 'react';
import api from '@/api/client';
import { useAuth } from './AuthContext';

interface TenantInfo {
  id: string;
  name: string;
  vertical: string;
  plan: string;
  active: boolean;
  features: Record<string, boolean>;
  /** 'operator' = full overseer surface; 'simple' = tailored subscriber surface. */
  navMode: 'operator' | 'simple';
}

interface TenantState {
  tenantId: string | null;
  tenant: TenantInfo | null;
  isLoading: boolean;
  /** Missing key = enabled — only an explicit false disables a feature. */
  hasFeature: (key: string) => boolean;
  /** True when this tenant is a subscriber on the tailored (simple) surface. */
  isSimpleMode: boolean;
  /** Agent kinds this tenant actually runs (server-computed, feature-aware). */
  activeAgentKinds: string[];
  hasAgent: (kind: string) => boolean;
  switchTenant: (id: string) => Promise<void>;
}

const TenantContext = createContext<TenantState | undefined>(undefined);

function TenantProvider({ children }: { children: ReactNode }) {
  const { user, isAuthenticated } = useAuth();
  const [tenantId, setTenantId] = useState<string | null>(
    () => localStorage.getItem('tenant_id') ?? user?.tenantId ?? null,
  );
  const [tenant, setTenant] = useState<TenantInfo | null>(null);
  const [activeAgentKinds, setActiveAgentKinds] = useState<string[]>([]);
  const [isLoading, setIsLoading] = useState(false);

  const fetchTenant = useCallback(async (id: string) => {
    setIsLoading(true);
    // API-unavailable fallback: full operator surface, so nothing is ever
    // hidden by accident on a failed load.
    const fallback: TenantInfo = {
      id, name: user?.email?.split('@')[0] ?? 'My Business',
      vertical: 'general', plan: 'starter', active: true, features: {}, navMode: 'operator',
    };
    try {
      // Session-scoped tenant record incl. feature flags + navMode + live agents.
      const res = await api.get<{ tenant: TenantInfo; activeAgentKinds?: string[] }>('/tenant/me');
      if (res.data?.tenant) {
        setTenant({ ...res.data.tenant, navMode: res.data.tenant.navMode === 'simple' ? 'simple' : 'operator' });
        setActiveAgentKinds(res.data.activeAgentKinds ?? []);
      } else {
        setTenant(fallback);
        setActiveAgentKinds([]);
      }
    } catch {
      setTenant(fallback);
      setActiveAgentKinds([]);
    } finally {
      setIsLoading(false);
    }
  }, [user?.email]);

  const hasFeature = useCallback(
    (key: string) => tenant?.features?.[key] !== false,
    [tenant],
  );

  const isSimpleMode = tenant?.navMode === 'simple';
  const hasAgent = useCallback(
    (kind: string) => activeAgentKinds.includes(kind),
    [activeAgentKinds],
  );

  const switchTenant = useCallback(
    async (id: string) => {
      setTenantId(id);
      localStorage.setItem('tenant_id', id);
      await fetchTenant(id);
    },
    [fetchTenant],
  );

  // Fetch tenant info ONCE when authenticated
  const hasFetchedRef = React.useRef(false);
  useEffect(() => {
    if (!isAuthenticated || !user?.tenantId) return;
    if (hasFetchedRef.current) return;
    hasFetchedRef.current = true;

    const id = tenantId ?? user.tenantId;
    if (!tenantId) {
      setTenantId(id);
      localStorage.setItem('tenant_id', id);
    }
    fetchTenant(id);
  }, [isAuthenticated, user?.tenantId]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <TenantContext.Provider value={{ tenantId, tenant, isLoading, hasFeature, isSimpleMode, activeAgentKinds, hasAgent, switchTenant }}>
      {children}
    </TenantContext.Provider>
  );
}

function useTenant(): TenantState {
  const ctx = useContext(TenantContext);
  if (!ctx) throw new Error('useTenant must be used within TenantProvider');
  return ctx;
}

export { TenantProvider, useTenant };
export default TenantContext;
